import { deserializePropertyInput } from '@/modules/Project/Properties/deserializePropertyInput'
import type { Property } from '@/modules/Project/Properties/types'
import { post } from './client'
import type { BackendMeta } from './types'

export type AddPropertyMeta = BackendMeta<
  '/api/workspaces/{workspace_id}/projects/{project_id}/properties',
  'post'
>

export type AddPropertyPath = AddPropertyMeta['path']
export type AddPropertyRequest = Omit<AddPropertyMeta['body'], 'type'> & {
  type: Exclude<AddPropertyMeta['body']['type'], 'integration'>
}
export type AddPropertyResponse = AddPropertyMeta['successResponse']

type BodyWithConfig = Extract<AddPropertyMeta['body'], { config: unknown }>
type BodyWithoutConfig = Exclude<AddPropertyMeta['body'], { config: unknown }>

export const addPropertyConfig = (
  params: Property,
): Pick<BodyWithConfig, 'type' | 'config'> | Pick<BodyWithoutConfig, 'type'> => {
  if (
    params.type === 'single_select' ||
    params.type === 'multi_select' ||
    params.type === 'user_select'
  ) {
    return {
      type: params.type,
      config: { options: params.config.options, default_option: params.config.defaultOption },
    }
  }

  if (params.type === 'hub_select') {
    return {
      type: params.type,
      config: {
        options: params.config.options,
        default_option: params.config.defaultOption,
        max_selected: params.config.maxSelected,
      },
    }
  }

  if (params.type === 'reference') {
    if (!params.config.projectId) {
      throw new Error('projectId is required for reference property')
    }
    return {
      type: params.type,
      config: {
        entity_limit: params.config.entityLimit || 1,
        project_id: params.config.projectId,
      },
    }
  }

  if (params.type === 'number') {
    const customFormat = params.config.isCustomFormat ? params.config.format : undefined
    return {
      type: params.type,
      config: {
        format: params.config.isCustomFormat ? 'custom' : 'auto',
        format_custom: customFormat
          ? {
              decimal_places: customFormat.decimalPlaces,
              negative_format: customFormat.negativeFormat,
              right_align: customFormat.rightAlign,
              thousand_separator: customFormat.thousandsSeparator ? 'comma' : 'none',
            }
          : undefined,
      },
    }
  }

  if (params.type === 'collection') {
    return {
      type: params.type,
      config: {
        properties: params.config?.properties?.map((p) => ({ name: p.name, type: p.type })) || [],
      },
    }
  }

  return { type: params.type }
}

export const addProperty = ({
  projectId,
  property,
  workspaceId,
  viewId,
}: {
  workspaceId: string
  projectId: string
  property: Property
  viewId?: string
}) =>
  post<AddPropertyRequest, AddPropertyResponse, AddPropertyPath>(
    `/api/workspaces/${workspaceId}/projects/${projectId}/properties`,
    {
      name: property.name,
      tool: property.tool,
      is_grounded: property.isGrounded,
      description: property.description,
      default_view_id: viewId,
      inputs: property.inputs.map(deserializePropertyInput),
      ...addPropertyConfig(property),
    },
  )
